import { useState, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface UseAddToProjectOptions {
  onSuccess?: (projectId: string | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for managing project assignment for private research
 * UPDATED: Uses direct workbook_id assignment on workbook_research table
 */
export const useAddToProject = (itemId: string, options: UseAddToProjectOptions = {}) => {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // State for optimistic updates only
  const [optimisticProjectId, setOptimisticProjectId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Query for current project assignment using direct workbook_id
  const projectAssignmentQuery = useQuery({
    queryKey: ['research-project-assignment', itemId],
    queryFn: async () => {
      if (!user?.id) return { projectId: null, isOwner: false };
      
      try {
        // Get research with workbook_id directly
        const { data: research, error: researchError } = await supabase
          .from('workbook_research')
          .select('user_id, title, workbook_id')
          .eq('id', itemId)
          .single();

        if (researchError) {
          console.warn('Failed to get research info:', researchError);
          return { projectId: null, isOwner: false };
        }

        const isOwner = research.user_id === user.id;

        return { 
          projectId: research.workbook_id, 
          isOwner,
          researchTitle: research.title 
        };
      } catch (error) {
        console.warn('Failed to get project assignment:', error);
        return { projectId: null, isOwner: false };
      }
    },
    enabled: !!user?.id && !!itemId,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Get effective project ID (optimistic or actual)
  const currentProjectId = hasOptimisticUpdate ? optimisticProjectId : projectAssignmentQuery.data?.projectId || null;
  const isOwner = projectAssignmentQuery.data?.isOwner || false;
  const researchTitle = projectAssignmentQuery.data?.researchTitle || '';

  // Mutation for updating research project assignment via direct workbook_id
  const updateMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Directly update workbook_id on the workbook_research table
      const { error } = await supabase
        .from('workbook_research')
        .update({ workbook_id: projectId })
        .eq('id', itemId)
        .eq('user_id', user.id); // Security check

      if (error) {
        throw error;
      }

      return projectId;
    },
    onMutate: async (projectId: string | null) => {
      // Optimistic update
      setOptimisticProjectId(projectId);
      setHasOptimisticUpdate(true);
    },
    onSuccess: (data, projectId) => {
      // Clear optimistic update
      setHasOptimisticUpdate(false);
      
      // Comprehensive cache invalidation for junction table approach
      // Invalidate private research generator caches
      queryClient.invalidateQueries({ queryKey: ['private-research-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-research-generator-count'] });
      
      // Invalidate project research queries (since junction table affects visibility)
      queryClient.invalidateQueries({ queryKey: ['generated-research-details'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-research'] });
      
      // Invalidate goal/project related caches
      queryClient.invalidateQueries({ queryKey: ['suggested-research'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['suggestedResearch'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['generated-research'], exact: false });
      
      // Invalidate this research's project assignment query to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['research-project-assignment', itemId] });
      
      // CRITICAL: Invalidate the infinite user research sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-research'] });
      
      // Also invalidate images cache for cross-content compatibility
      queryClient.invalidateQueries({ queryKey: ['infinite-user-images'] });
      
      // Success feedback
      toast({
        title: "Success",
        description: projectId ? "Research assigned to project" : "Research removed from project",
      });
      
      options.onSuccess?.(projectId);
    },
    onError: (error: any, projectId) => {
      // Revert optimistic update
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      
      // Check if this is a permission error and show appropriate message
      const isPermissionError = error.message?.includes('You can only modify') || 
                               error.message?.includes('permission') ||
                               error.message?.includes('access denied');
      
      // Error feedback with better messaging
      toast({
        title: isPermissionError ? "Permission Denied" : "Error",
        description: isPermissionError 
          ? error.message 
          : `Failed to ${projectId ? 'assign to' : 'remove from'} project: ${error.message}`,
        variant: "destructive",
      });
      
      options.onError?.(error);
    },
  });

  // Helper functions
  const updateResearchProject = useCallback(async (projectId: string | null) => {
    if (updateMutation.isPending) return;
    await updateMutation.mutateAsync(projectId);
  }, [updateMutation]);

  const removeFromProject = useCallback(async () => {
    if (updateMutation.isPending) return;
    await updateMutation.mutateAsync(null);
  }, [updateMutation]);

  return {
    currentProjectId,
    updateResearchProject,
    removeFromProject,
    isPending: updateMutation.isPending,
    isLoading: updateMutation.isPending || projectAssignmentQuery.isLoading,
    error: updateMutation.error || projectAssignmentQuery.error,
    isOwner,
    // Expose query state for debugging
    isProjectAssignmentLoading: projectAssignmentQuery.isLoading,
    projectAssignmentError: projectAssignmentQuery.error,
  };
}; 